import React from 'react';

import { Box, Button, Flex, Text } from '@rover/kibble/core';

import {
  BADGE_DIGIT_OPTICAL_OFFSET,
  BADGE_RING_PX,
  BUTTON_SIZE_TO_BADGE_OFFSET_MAP,
  BUTTON_SIZE_TO_BADGE_SIZE_MAP,
  ButtonWithCountBadgeWebProps,
  getBadgeLabel,
  isPillBadge,
} from './ButtonWithCountBadge.common';

/** An icon-only Button with a count on its top-right corner. */
const ButtonWithCountBadge = ({
  badgeCount,
  ariaLabel,
  badgeTestID,
  hideBadgeFromScreenReader = false,
  onClick,
  size = 'default',
  ...otherProps
}: ButtonWithCountBadgeWebProps): JSX.Element => {
  const badgeSize = BUTTON_SIZE_TO_BADGE_SIZE_MAP[size];
  const { top, right } = BUTTON_SIZE_TO_BADGE_OFFSET_MAP[size];
  const badgeLabel = getBadgeLabel(badgeCount);
  const isPill = isPillBadge(badgeLabel, size);

  return (
    <Flex alignItems="center">
      <Box position="relative">
        {badgeCount ? (
          <Box
            data-testid={badgeTestID}
            background="notification"
            borderColor="border.surface"
            borderStyle="solid"
            borderRadius="round"
            justifyContent="center"
            alignItems="center"
            height={badgeSize}
            width={isPill ? 'auto' : badgeSize}
            minWidth={badgeSize}
            px={isPill ? '1x' : '0x'}
            webProps={hideBadgeFromScreenReader ? { 'aria-hidden': true } : undefined}
            sx={{
              position: 'absolute',
              borderWidth: 'primary',
              top: top - BADGE_RING_PX,
              left: `calc(100% - ${right + badgeSize + BADGE_RING_PX}px)`,
              zIndex: 1,
              display: 'flex',
              boxSizing: 'content-box',
            }}
          >
            <Text
              size="50"
              fontWeight="semibold"
              textColor="primary_inverse"
              textAlign="center"
              sx={{
                lineHeight: 1,
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
                transform: `translateY(${BADGE_DIGIT_OPTICAL_OFFSET}px)`,
              }}
            >
              {badgeLabel}
            </Text>
          </Box>
        ) : null}
        <Button onClick={onClick} size={size} aria-label={ariaLabel} {...otherProps} />
      </Box>
    </Flex>
  );
};

export default ButtonWithCountBadge;
